<template>
  <header class="fixed top-0 left-0 right-0 bg-primary shadow-sm z-[999]">
    <div class="flex items-center justify-between h-16 px-4">
      <!-- 左侧返回按钮 -->
      <button 
        v-if="showBack"
        @click="$emit('back')"
        class="flex items-center justify-center w-9 h-9 rounded-lg hover:bg-white hover:bg-opacity-20 transition-all duration-200"
      >
        <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
        </svg>
      </button>
      
      <!-- 左侧LOGO和品牌 -->
      <div v-else class="flex items-center space-x-3">
        <!-- LOGO图标 -->
        <div class="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center backdrop-blur-sm">
          <svg class="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
            <path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z"/>
          </svg>
        </div>
        <!-- 品牌名称 -->
        <div class="flex flex-col">
          <span class="text-white font-bold text-lg leading-tight">内府云途</span>
          <span class="text-emerald-100 text-xs leading-tight">ERP</span>
        </div>
      </div>
      
      <!-- 中间标题 -->
      <h1 v-if="showBack" class="text-lg font-semibold text-white truncate mx-4">
        {{ title }}
      </h1>
      
      <!-- 右侧按钮组 -->
      <div class="flex items-center space-x-2">
        <!-- 消息按钮 -->
        <button 
          @click="$emit('message')"
          class="flex items-center justify-center w-9 h-9 rounded-lg hover:bg-white hover:bg-opacity-20 transition-all duration-200 relative"
        >
          <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-5 5v-5zM11 19H6a2 2 0 01-2-2V7a2 2 0 012-2h12a2 2 0 012 2v5" />
          </svg>
          <!-- 未读消息提示点 -->
          <div v-if="unreadCount > 0" class="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-xs flex items-center justify-center rounded-full">
            {{ unreadCount > 9 ? '9+' : unreadCount }}
          </div>
        </button>
        
        <!-- 菜单按钮 -->
        <button 
          @click="showMenu = !showMenu"
          class="flex items-center justify-center w-9 h-9 rounded-lg hover:bg-white hover:bg-opacity-20 transition-all duration-200 relative"
        >
        <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zM12 13a1 1 0 110-2 1 1 0 010 2zM12 20a1 1 0 110-2 1 1 0 010 2z" />
        </svg>
        
        <!-- 下拉菜单 -->
        <div 
          v-if="showMenu"
          class="absolute top-full right-0 mt-3 w-48 bg-white rounded-xl shadow-xl border border-gray-100 py-2 backdrop-blur-sm z-[999]"
          @click.stop
        >
          <router-link 
            to="/mobile/settings"
            class="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            @click="showMenu = false"
          >
            <svg class="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            系统设置
          </router-link>
          
          <router-link 
            to="/mobile/profile"
            class="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            @click="showMenu = false"
          >
            <svg class="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            个人中心
          </router-link>
          
          <div class="border-t border-gray-100 my-1"></div>
          
          <button 
            @click="handleLogout"
            class="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
          >
            <svg class="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            退出登录
          </button>
        </div>
      </button>
      </div>
    </div>
    
    <!-- 点击遮罩关闭菜单 -->
    <div 
      v-if="showMenu"
      class="fixed inset-0 z-[998]"
      @click="showMenu = false"
    ></div>
  </header>
</template>

<script>
import { ref } from 'vue'
import { useRouter } from 'vue-router'

export default {
  name: 'MobileHeader',
  props: {
    title: {
      type: String,
      default: '内府云途ERP'
    },
    showBack: {
      type: Boolean,
      default: false
    },
    unreadCount: {
      type: Number,
      default: 0
    }
  },
  emits: ['back', 'message'],
  setup() {
    const router = useRouter()
    const showMenu = ref(false)
    
    const handleLogout = () => {
      showMenu.value = false
      // 清除登录状态
      localStorage.removeItem('token')
      localStorage.removeItem('userInfo')
      // 跳转到登录页
      router.push('/login')
      // 显示退出提示
      if (window.mobileApp) {
        window.mobileApp.showToast('已退出登录', 'success')
      }
    }
    
    return {
      showMenu,
      handleLogout
    }
  }
}
</script>

<style scoped>
/* 确保下拉菜单在最顶层 */
.absolute {
  z-index: 999;
}
</style>